import { useState } from "react";
import { Icon } from "@juiceforward/narrative-player";

const narrativeOptions = [
  { id: "all", title: "All Narratives" },
  { id: "narrative_001", title: "Family Engagement Progress Report" },
  { id: "narrative_002", title: "Smart Insights 2026 Year in Review" },
  { id: "narrative_003", title: "GOLD Assessment Insights" },
  { id: "narrative_004", title: "Classroom Learning Outcomes Review" },
];

export const AnalyticsView = () => {
  const [selectedNarrative, setSelectedNarrative] = useState("all");

  const analyticsData = {
    totalViews: 1496,
    uniqueViewers: 892,
    avgTimeSpent: "4m 32s",
    completionRate: "67%",
  };

  const topNarratives = [
    { title: "Quarterly Business Review", views: 789, completion: 72 },
    { title: "Product Usage Analysis", views: 456, completion: 65 },
    { title: "Q4 2025 Executive Summary", views: 234, completion: 58 },
  ];

  const recentActivity = [
    {
      user: "john@acme.com",
      action: "Viewed",
      narrative: "Q4 2025 Executive Summary",
      time: "5 min ago",
    },
    {
      user: "sarah@techstart.com",
      action: "Bookmarked",
      narrative: "Customer Health Metrics",
      time: "12 min ago",
    },
    {
      user: "mike@global.com",
      action: "Shared",
      narrative: "Product Usage Analysis",
      time: "28 min ago",
    },
    {
      user: "emily@innovation.com",
      action: "Completed",
      narrative: "Quarterly Business Review",
      time: "1 hour ago",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Narrative Filter */}
      <div className="flex items-center gap-4">
        <label className="text-sm text-[var(--admin-text-muted)]">
          Filter by Narrative:
        </label>
        <select
          value={selectedNarrative}
          onChange={(e) => setSelectedNarrative(e.target.value)}
          className="bg-[var(--admin-surface)] border border-[var(--admin-border-secondary)] rounded-lg px-4 py-2.5 text-[var(--admin-text)] focus:outline-none focus:border-emerald-500 min-w-[300px]"
        >
          {narrativeOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.title}
            </option>
          ))}
        </select>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-[var(--admin-surface)] border border-[var(--admin-border)] rounded-lg p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[var(--admin-text-muted)] text-sm">
              Total Views
            </div>
            <Icon icon="eye" className="text-emerald-400" />
          </div>
          <div className="text-2xl font-bold">
            {analyticsData.totalViews.toLocaleString()}
          </div>
          <div className="text-xs text-emerald-400 mt-1">
            ↑ 12% from last month
          </div>
        </div>
        <div className="bg-[var(--admin-surface)] border border-[var(--admin-border)] rounded-lg p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[var(--admin-text-muted)] text-sm">
              Unique Viewers
            </div>
            <Icon icon="users" className="text-blue-400" />
          </div>
          <div className="text-2xl font-bold">
            {analyticsData.uniqueViewers.toLocaleString()}
          </div>
          <div className="text-xs text-emerald-400 mt-1">
            ↑ 8% from last month
          </div>
        </div>
        <div className="bg-[var(--admin-surface)] border border-[var(--admin-border)] rounded-lg p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[var(--admin-text-muted)] text-sm">
              Avg Time Spent
            </div>
            <Icon icon="clock" className="text-purple-400" />
          </div>
          <div className="text-2xl font-bold">{analyticsData.avgTimeSpent}</div>
          <div className="text-xs text-emerald-400 mt-1">
            ↑ 15% from last month
          </div>
        </div>
        <div className="bg-[var(--admin-surface)] border border-[var(--admin-border)] rounded-lg p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[var(--admin-text-muted)] text-sm">
              Completion Rate
            </div>
            <Icon icon="check-circle" className="text-yellow-400" />
          </div>
          <div className="text-2xl font-bold">
            {analyticsData.completionRate}
          </div>
          <div className="text-xs text-red-400 mt-1">↓ 3% from last month</div>
        </div>
      </div>

      {/* Charts Placeholder */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-[var(--admin-surface)] border border-[var(--admin-border)] rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Views Over Time</h3>
          <div className="h-64 flex items-center justify-center text-[var(--admin-text-subtle)] border-2 border-dashed border-[var(--admin-border-secondary)] rounded">
            <div className="text-center">
              <Icon icon="chart-line" className="text-4xl mb-2" />
              <p>Chart visualization placeholder</p>
            </div>
          </div>
        </div>
        <div className="bg-[var(--admin-surface)] border border-[var(--admin-border)] rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Engagement by Section</h3>
          <div className="h-64 flex items-center justify-center text-[var(--admin-text-subtle)] border-2 border-dashed border-[var(--admin-border-secondary)] rounded">
            <div className="text-center">
              <Icon icon="chart-bar" className="text-4xl mb-2" />
              <p>Chart visualization placeholder</p>
            </div>
          </div>
        </div>
      </div>

      {/* Top Performing Narratives */}
      <div className="bg-[var(--admin-surface)] border border-[var(--admin-border)] rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">
          Top Performing Narratives
        </h3>
        <div className="space-y-4">
          {topNarratives.map((narrative, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                <div className="text-2xl font-bold text-[var(--admin-text-subtle)]">
                  #{index + 1}
                </div>
                <div className="flex-1">
                  <div className="font-medium">{narrative.title}</div>
                  <div className="text-sm text-[var(--admin-text-subtle)]">
                    {narrative.views} views
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-sm text-[var(--admin-text-muted)]">
                    Completion
                  </div>
                  <div className="text-lg font-semibold text-emerald-400">
                    {narrative.completion}%
                  </div>
                </div>
                <div className="w-24 bg-[var(--admin-border-secondary)] rounded-full h-2">
                  <div
                    className="bg-emerald-500 h-2 rounded-full"
                    style={{ width: `${narrative.completion}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-[var(--admin-surface)] border border-[var(--admin-border)] rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
        <div className="space-y-3">
          {recentActivity.map((activity, index) => (
            <div
              key={index}
              className="flex items-start gap-3 pb-3 border-b border-[var(--admin-border)] last:border-0"
            >
              <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon icon="user" className="text-emerald-400 text-sm" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm">
                  <span className="font-medium">{activity.user}</span>
                  <span className="text-[var(--admin-text-muted)]">
                    {" "}
                    {activity.action.toLowerCase()}{" "}
                  </span>
                  <span className="font-medium">{activity.narrative}</span>
                </div>
                <div className="text-xs text-[var(--admin-text-subtle)] mt-1">
                  {activity.time}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
